import { useEffect, useState } from 'react';
import { Card, Row, Col, Form, Button, Alert, Badge, Table, Spinner } from 'react-bootstrap';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const LEVELS = ['beginner', 'intermediate', 'expert'];

export default function TeacherProfile() {
  const { refresh } = useAuth();
  const [profile, setProfile] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [regFee, setRegFee] = useState(0);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [p, s, r] = await Promise.all([
        api.get('/teacher/profile'),
        api.get('/teacher/subjects/available'),
        api.get('/teacher/registration-fee'),
      ]);
      setProfile(p);
      setSubjects(s);
      setRegFee(Number(r.amount));
    } catch (e) {
      setMsg({ type: 'danger', text: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-center py-5"><Spinner /></div>;
  if (!profile) return <Alert variant="danger">{msg.text || 'Profile not found'}</Alert>;

  return (
    <>
      <h3 className="mb-4">My Profile</h3>
      {msg.text && <Alert variant={msg.type} onClose={() => setMsg({ type: '', text: '' })} dismissible>{msg.text}</Alert>}

      <StatusCard profile={profile} regFee={regFee} onPaid={load} setMsg={setMsg} refresh={refresh} />
      <PersonalCard profile={profile} onSaved={load} setMsg={setMsg} />
      <SubjectsCard profile={profile} allSubjects={subjects} onSaved={load} setMsg={setMsg} />
      <AvailabilityCard profile={profile} onSaved={load} setMsg={setMsg} />
    </>
  );
}

function StatusCard({ profile, regFee, onPaid, setMsg, refresh }) {
  const [busy, setBusy] = useState(false);
  const status = profile.status;
  const paid = !!Number(profile.registration_fee_paid);

  const pay = async () => {
    if (!window.confirm(`Pay registration fee of ₹${regFee}? (manual placeholder — will be replaced by gateway later)`)) return;
    setBusy(true);
    try {
      await api.post('/teacher/registration-fee');
      setMsg({ type: 'success', text: 'Registration fee paid.' });
      await onPaid();
      await refresh();
    } catch (e) {
      setMsg({ type: 'danger', text: e.message });
    } finally {
      setBusy(false);
    }
  };

  let statusEl;
  if (status === 'pending')       statusEl = <Badge bg="warning">Pending admin approval</Badge>;
  else if (status === 'approved') statusEl = <Badge bg="success">Approved</Badge>;
  else if (status === 'rejected') statusEl = <Badge bg="danger">Rejected</Badge>;
  else                            statusEl = <Badge bg="secondary">{status}</Badge>;

  return (
    <Card className="shadow-sm mb-4">
      <Card.Body>
        <Card.Title>Account status</Card.Title>
        <p className="mb-2">Profile: {statusEl}</p>
        <p className="mb-2">
          Registration fee (₹{regFee.toFixed(0)}):{' '}
          {paid ? <Badge bg="success">Paid</Badge> : <Badge bg="warning">Not paid</Badge>}
        </p>
        {!paid && (
          <Button variant="primary" onClick={pay} disabled={busy}>
            {busy ? 'Processing...' : `Pay ₹${regFee.toFixed(0)} now`}
          </Button>
        )}
        {paid && status === 'pending' && (
          <p className="small text-muted mb-0">Your profile is awaiting admin approval. Once approved, students can discover you.</p>
        )}
      </Card.Body>
    </Card>
  );
}

function PersonalCard({ profile, onSaved, setMsg }) {
  const [f, setF] = useState({
    name: profile.name || '',
    bio: profile.bio || '',
    qualifications: profile.qualifications || '',
    experience_years: profile.experience_years || 0,
    city: profile.city || '',
    locality: profile.locality || '',
    photo: profile.photo || '',
    monthly_fee: profile.monthly_fee || 0,
    demo_fee: profile.demo_fee || 0,
  });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put('/teacher/profile', {
        ...f,
        experience_years: Number(f.experience_years) || 0,
        monthly_fee: Number(f.monthly_fee) || 0,
        demo_fee: Number(f.demo_fee) || 0,
      });
      setMsg({ type: 'success', text: 'Profile saved.' });
      await onSaved();
    } catch (e2) {
      setMsg({ type: 'danger', text: e2.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="shadow-sm mb-4">
      <Card.Body>
        <Card.Title>Personal details & rates</Card.Title>
        <Form onSubmit={save}>
          <Row>
            <Col md={6}><Form.Group className="mb-3"><Form.Label>Full name</Form.Label><Form.Control value={f.name} onChange={set('name')} required /></Form.Group></Col>
            <Col md={6}><Form.Group className="mb-3"><Form.Label>Qualifications</Form.Label><Form.Control value={f.qualifications} onChange={set('qualifications')} placeholder="e.g., MSc Physics" /></Form.Group></Col>
          </Row>
          <Form.Group className="mb-3"><Form.Label>Bio</Form.Label><Form.Control as="textarea" rows={3} value={f.bio} onChange={set('bio')} placeholder="Short intro students will see" /></Form.Group>
          <Row>
            <Col md={4}><Form.Group className="mb-3"><Form.Label>Experience (years)</Form.Label><Form.Control type="number" min={0} value={f.experience_years} onChange={set('experience_years')} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label>City</Form.Label><Form.Control value={f.city} onChange={set('city')} placeholder="e.g., Chennai" /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label>Locality</Form.Label><Form.Control value={f.locality} onChange={set('locality')} /></Form.Group></Col>
          </Row>
          <Row>
            <Col md={4}><Form.Group className="mb-3"><Form.Label>Monthly fee (₹)</Form.Label><Form.Control type="number" min={0} value={f.monthly_fee} onChange={set('monthly_fee')} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label>Demo fee (₹)</Form.Label><Form.Control type="number" min={0} value={f.demo_fee} onChange={set('demo_fee')} /></Form.Group></Col>
            <Col md={4}><Form.Group className="mb-3"><Form.Label>Photo URL</Form.Label><Form.Control value={f.photo} onChange={set('photo')} placeholder="https://..." /></Form.Group></Col>
          </Row>
          <Button type="submit" disabled={busy}>{busy ? 'Saving...' : 'Save details'}</Button>
        </Form>
      </Card.Body>
    </Card>
  );
}

function SubjectsCard({ profile, allSubjects, onSaved, setMsg }) {
  const initial = Object.fromEntries((profile.subjects || []).map(s => [s.subject_id, s.expertise_level]));
  const [selected, setSelected] = useState(initial);
  const [busy, setBusy] = useState(false);

  const toggle = (id) => {
    const next = { ...selected };
    if (next[id]) delete next[id];
    else next[id] = 'intermediate';
    setSelected(next);
  };
  const setLevel = (id, lvl) => setSelected({ ...selected, [id]: lvl });

  const save = async () => {
    setBusy(true);
    try {
      const payload = Object.entries(selected).map(([sid, lvl]) => ({ subject_id: Number(sid), expertise_level: lvl }));
      await api.post('/teacher/subjects', { subjects: payload });
      setMsg({ type: 'success', text: `Subjects saved (${payload.length}).` });
      await onSaved();
    } catch (e) {
      setMsg({ type: 'danger', text: e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="shadow-sm mb-4">
      <Card.Body>
        <Card.Title>Subjects I teach</Card.Title>
        <p className="small text-muted">Tick subjects you teach, then pick your expertise level.</p>
        <Row>
          {allSubjects.map((s) => (
            <Col md={6} key={s.id} className="mb-2">
              <Form.Check
                type="checkbox"
                id={`subj-${s.id}`}
                label={s.name}
                checked={!!selected[s.id]}
                onChange={() => toggle(s.id)}
              />
              {selected[s.id] && (
                <div className="ms-4">
                  {LEVELS.map((lvl) => (
                    <Form.Check
                      inline
                      type="radio"
                      key={lvl}
                      name={`level-${s.id}`}
                      id={`level-${s.id}-${lvl}`}
                      label={lvl}
                      checked={selected[s.id] === lvl}
                      onChange={() => setLevel(s.id, lvl)}
                    />
                  ))}
                </div>
              )}
            </Col>
          ))}
        </Row>
        <Button onClick={save} disabled={busy}>{busy ? 'Saving...' : 'Save subjects'}</Button>
      </Card.Body>
    </Card>
  );
}

function AvailabilityCard({ profile, onSaved, setMsg }) {
  const [slots, setSlots] = useState(
    (profile.availability || []).map((a) => ({
      day_of_week: Number(a.day_of_week),
      start_time: (a.start_time || '').slice(0, 5),
      end_time: (a.end_time || '').slice(0, 5),
    }))
  );
  const [busy, setBusy] = useState(false);

  const addSlot = () => setSlots([...slots, { day_of_week: 1, start_time: '10:00', end_time: '11:00' }]);
  const removeSlot = (i) => setSlots(slots.filter((_, idx) => idx !== i));
  const updateSlot = (i, k, v) => setSlots(slots.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));

  const save = async () => {
    setBusy(true);
    try {
      const payload = slots.map((s) => ({
        day_of_week: Number(s.day_of_week),
        start_time: s.start_time.length === 5 ? s.start_time + ':00' : s.start_time,
        end_time: s.end_time.length === 5 ? s.end_time + ':00' : s.end_time,
      }));
      await api.put('/teacher/availability', { availability: payload });
      setMsg({ type: 'success', text: `Availability saved (${payload.length} slot${payload.length === 1 ? '' : 's'}).` });
      await onSaved();
    } catch (e) {
      setMsg({ type: 'danger', text: e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="shadow-sm mb-4">
      <Card.Body>
        <Card.Title>Weekly availability</Card.Title>
        <p className="small text-muted">Time slots when students can book a demo with you.</p>
        {slots.length > 0 && (
          <Table bordered size="sm" responsive className="mb-3">
            <thead><tr><th>Day</th><th>Start</th><th>End</th><th style={{width: 80}}></th></tr></thead>
            <tbody>
              {slots.map((s, i) => (
                <tr key={i}>
                  <td>
                    <Form.Select value={s.day_of_week} onChange={(e) => updateSlot(i, 'day_of_week', Number(e.target.value))}>
                      {DAYS.map((d, idx) => <option key={idx} value={idx}>{d}</option>)}
                    </Form.Select>
                  </td>
                  <td><Form.Control type="time" value={s.start_time} onChange={(e) => updateSlot(i, 'start_time', e.target.value)} /></td>
                  <td><Form.Control type="time" value={s.end_time} onChange={(e) => updateSlot(i, 'end_time', e.target.value)} /></td>
                  <td><Button size="sm" variant="outline-danger" onClick={() => removeSlot(i)}>Remove</Button></td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        <div className="d-flex gap-2">
          <Button variant="outline-primary" onClick={addSlot}>+ Add slot</Button>
          <Button onClick={save} disabled={busy}>{busy ? 'Saving...' : 'Save availability'}</Button>
        </div>
      </Card.Body>
    </Card>
  );
}
